import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, FileText, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LOGO_WHITE = "https://customer-assets-jai6qajn.emergentagent.net/job_type-race-4/artifacts/a9pwlfiw_white_text_racing.png";
const HERO = "https://customer-assets-jai6qajn.emergentagent.net/job_type-race-4/artifacts/ti4o8uk7_HeroTR.jpg";
const BANNER = "https://customer-assets-jai6qajn.emergentagent.net/job_type-race-4/artifacts/ae8fpnjb_exportracemomentphoto.jpg";
const EDU_COVER = "/edutainment-cover.jpg";

const INK = "#0b0b0c", PAPER = "#f5f4f0", STEEL = "#17171a";
const LINE = "#2a2a2e", LINEP = "#d8d6cf", MUTED = "#9a9a96", MUTEDP = "#5c5b56";

const display = "font-['Big_Shoulders_Display'] font-black uppercase";
const mono = "font-['JetBrains_Mono']";
const body = "font-['Archivo']";
const balance = { textWrap: "balance" };

/* CTA destinations are computed per-auth inside the component */

/* ---- shared bits ---- */
const Checker = () => (
  <div aria-hidden className="w-full h-4" style={{
    backgroundColor: "#fff",
    backgroundImage:
      "linear-gradient(45deg,#000 25%,transparent 25%),linear-gradient(-45deg,#000 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#000 75%),linear-gradient(-45deg,transparent 75%,#000 75%)",
    backgroundSize: "16px 16px",
    backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
  }} />
);

const Eyebrow = ({ children, onDark }) => (
  <div className={`${mono} text-[11px] uppercase tracking-[0.18em] flex items-center gap-2 mb-5`}
    style={{ color: onDark ? MUTED : MUTEDP }}>
    <span className="inline-block h-px w-[18px]" style={{ background: "currentColor" }} />
    {children}
  </div>
);

const Section = ({ dark, children, id, className = "" }) => (
  <section id={id} className={`px-6 md:px-8 ${className}`}
    style={{ background: dark ? INK : PAPER, color: dark ? PAPER : INK }}>
    <div className="mx-auto py-20 md:py-28" style={{ maxWidth: 1120 }}>{children}</div>
  </section>
);

const Solid = ({ to, dark, children, testid, compact }) => (
  <Link to={to} data-testid={testid}
    className={`${body} font-semibold inline-flex items-center justify-center ${compact ? "px-4 py-2 text-xs" : "px-7 py-3.5 text-sm"} tracking-wide transition-opacity duration-150 hover:opacity-80`}
    style={{ background: dark ? PAPER : INK, color: dark ? INK : PAPER }}>
    {children}
  </Link>
);

const Ghost = ({ to, dark, children, testid }) => (
  <Link to={to} data-testid={testid}
    className={`${body} font-semibold inline-flex items-center justify-center px-7 py-3.5 text-sm tracking-wide border transition-colors duration-150`}
    style={{ borderColor: dark ? PAPER : INK, color: dark ? PAPER : INK }}>
    {children}
  </Link>
);

const Highlight = ({ children, dark }) => (
  <span className="inline-block px-3 -skew-x-3" style={{ background: dark ? PAPER : INK, color: dark ? INK : PAPER }}>
    {children}
  </span>
);

/* thin-gap tile grid handled by CardGridResponsive below */

function Faq() {
  const [open, setOpen] = useState(0);
  const items = [
    ["Is Text Racing really free?", "Yes. You can create an account and compete in free races without paying an entry fee. Optional paid races are also available for eligible players."],
    ["How does a Text Race work?", "Every racer sees the same line at the same time. When the race starts, text it as fast and accurately as you can. The fastest, most accurate racer to finish wins."],
    ["Do I have to enter cash races?", "No. Play free races, compete for sponsored prizes, and climb the rankings without entering paid races."],
    ["How do I win?", "Speed matters, but so does accuracy. The fastest eligible racer with the required accuracy wins according to that race's rules."],
    ["How do I get paid?", "Eligible winnings are credited to your Text Racing balance and can be withdrawn using available payout methods, subject to applicable rules and requirements."],
    ["Do I need to be a fast texter already?", "No. Start with free races, learn the keyboard, improve your speed, and see how you stack up."],
    ["Can Text Racing help improve literacy?", "Yes. Text Racing's speed-and-accuracy format makes it a fun way to gamify literacy, language practice, and education for learners of any age."],
    ["What devices are supported?", "Text Racing is available on supported iOS and Android devices."],
  ];
  return (
    <div className="mt-10">
      {items.map(([q, a], i) => {
        const isOpen = open === i;
        return (
          <div key={i} style={{ borderTop: `1px solid ${LINEP}` }}>
            <button onClick={() => setOpen(isOpen ? -1 : i)} data-testid={`faq-${i}`}
              className={`${body} w-full flex items-center justify-between gap-4 py-5 text-left font-semibold text-[15px] md:text-base`}
              style={{ color: INK }}>
              {q}
              <Plus size={20} className="shrink-0 transition-transform duration-200"
                style={{ transform: isOpen ? "rotate(45deg)" : "none", color: MUTEDP }} />
            </button>
            <div className="overflow-hidden transition-[max-height] duration-300" style={{ maxHeight: isOpen ? 240 : 0 }}>
              <p className={`${body} pb-5 text-[15px] leading-relaxed`} style={{ color: MUTEDP }}>{a}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const authed = !!(user && user.role);
  const start = authed ? "/dashboard" : "/register";
  const join = authed ? "/dashboard" : "/register";
  const activateTo = authed ? "/activate-booklet" : "/login?next=%2Factivate-booklet";
  const [showBar, setShowBar] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setShowBar(window.scrollY > window.innerHeight * 0.9);
      setScrolled(window.scrollY > 10);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className={`${body}`} style={{ background: INK }}>
      {/* HEADER */}
      <header className="sticky top-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? "rgba(11,11,12,0.72)" : "transparent",
          backdropFilter: scrolled ? "blur(12px)" : "none",
          borderBottom: `1px solid ${scrolled ? LINE : "transparent"}`,
        }}>
        <div className={`mx-auto flex items-center justify-between px-6 md:px-8 transition-all duration-300 ${scrolled ? "h-16" : "h-20 md:h-24"}`} style={{ maxWidth: 1120 }}>
          <img src={LOGO_WHITE} alt="Text Racing"
            className={`w-auto transition-all duration-300 ${scrolled ? "h-7 md:h-8" : "h-10 sm:h-14 md:h-16"}`}
            data-testid="landing-logo" />
          <Solid to={start} dark compact testid="header-cta">{authed ? "Dashboard" : "Start Racing"}</Solid>
        </div>
      </header>

      {/* HERO */}
      <Section dark className="pt-4 md:pt-0">
        <div className="grid grid-cols-1 md:grid-cols-[45fr_55fr] gap-8 md:gap-12 md:items-center">
          <div className="order-1">
            <Eyebrow onDark>FREE TO PLAY · CASH RACES DAILY</Eyebrow>
            <h1 className={`${display} leading-[0.86] text-5xl sm:text-6xl lg:text-7xl mb-6`}
              style={{ ...balance, transform: "skewX(-4deg)", transformOrigin: "left" }}>
              Your texting speed<br />
              <Highlight dark>could pay off.</Highlight>
            </h1>
            <p className={`${body} text-base sm:text-lg font-semibold mb-2 max-w-md`} style={{ color: PAPER }}>
              Think you're fast on your phone? Race real people for cash, prizes, and bragging rights.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-6">
              <Solid to={start} dark testid="hero-cta">{authed ? "Enter Dashboard" : "Start Racing"}</Solid>
              <span className={`${mono} text-[11px] uppercase tracking-[0.15em]`} style={{ color: MUTED }}>No credit card required.</span>
            </div>
          </div>
          <div className="order-2">
            <img src={HERO} alt="Friends racing on their phones" className="w-full h-[240px] sm:h-[340px] md:h-[420px] object-cover grayscale border" style={{ borderColor: LINE }} />
          </div>
        </div>
      </Section>

      <Checker />

      {/* WHAT IS */}
      <Section>
        <Eyebrow>WHAT IS TEXT RACING?</Eyebrow>
        <h2 className={`${display} text-4xl sm:text-5xl lg:text-6xl leading-[0.9] mb-6`} style={balance}>
          You already know how to text.<br />Now learn how to race.
        </h2>
        <p className={`${body} text-lg font-semibold mb-5 max-w-2xl`} style={{ color: INK }}>
          Text Racing turns an everyday skill into a competitive sport.
        </p>
        <p className={`${body} text-[15px] leading-relaxed mb-5 max-w-3xl`} style={{ color: MUTEDP }}>
          Every racer competes using the Text Racing custom keyboard, creating a unique playing field where speed, accuracy, quick thinking, and knowledge all matter. Master the keyboard, sharpen your skills, and race other players for cash and prizes ranging from a few dollars to potentially thousands.
        </p>
        <p className={`${display} text-2xl`} style={balance}>Same skill. New playing field. Real competition.</p>
      </Section>

      <Checker />

      {/* HOW IT WORKS */}
      <Section dark>
        <Eyebrow onDark>HOW IT WORKS</Eyebrow>
        <h2 className={`${display} text-4xl sm:text-5xl lg:text-6xl leading-[0.9] mb-4`} style={balance}>
          From texting to the finish line
        </h2>
        <p className={`${body} text-[15px] mb-12 max-w-xl`} style={{ color: MUTED }}>
          You already know how to text. Now see how fast you really are.
        </p>
        <div className="grid gap-8 md:grid-cols-3">
          {[
            { n: "01", t: "Create Your Account", d: "Download Text Racing, create your free account, and get ready to compete." },
            { n: "02", t: "Join a Race", d: "Choose a free daily race or enter a cash race and compete for prizes." },
            { n: "03", t: "Race to Win", d: "Text with speed and accuracy, finish at the top, and claim your prize." },
          ].map((s, i) => (
            <div key={i} className="pt-4" style={{ borderTop: `2px solid ${LINE}` }} data-testid={`how-${i}`}>
              <div className={`${mono} text-3xl mb-3`}>{s.n}</div>
              <h3 className={`${display} text-2xl leading-none mb-2`} style={balance}>{s.t}</h3>
              <p className={`${body} text-[15px] leading-relaxed`} style={{ color: MUTED }}>{s.d}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-14"><Solid to={start} dark testid="how-cta">Start Your First Race</Solid></div>
      </Section>

      <Checker />

      {/* FULL-BLEED BANNER */}
      <div className="relative w-full overflow-hidden" style={{ height: "clamp(240px,42vw,440px)" }}>
        <img src={BANNER} alt="Race moment" className="absolute inset-0 w-full h-full object-cover grayscale" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,rgba(11,11,12,0.35),rgba(11,11,12,0.75))" }} />
        <div className="relative h-full flex flex-col items-center justify-center text-center px-6" style={{ color: PAPER }}>
          <div className={`${mono} text-[11px] uppercase tracking-[0.2em] mb-3`}>From two racers to thousands.</div>
          <div className={`${display} text-5xl sm:text-6xl lg:text-8xl leading-none`} style={balance}>READY. SET. TEXT.</div>
        </div>
      </div>

      <Checker />

      {/* WHY */}
      <Section>
        <Eyebrow>WHY TEXT RACING?</Eyebrow>
        <h2 className={`${display} text-4xl sm:text-5xl lg:text-6xl leading-[0.9] mb-5`} style={balance}>
          Your thumbs have been training for this.
        </h2>
        <p className={`${body} text-[15px] leading-relaxed mb-12 max-w-3xl`} style={{ color: MUTEDP }}>
          You text every day. But how fast are you when someone is racing against you? Text Racing transforms everyday texting into a competitive sport where every character, every second, and every mistake matters.
        </p>
        <CardGridResponsive cols="repeat(4,minmax(0,1fr))" mdBreak items={[
          { t: "Real cash prizes", d: "Win eligible races and earn real money. Your winnings are added directly to your Text Racing balance." },
          { t: "Live competition", d: "You're not racing a computer. Compete against real players in real time and see who's actually faster." },
          { t: "Free races every day", d: "You don't have to spend money to compete, but you can still win cash and prizes by jumping into our free contests that happen monthly." },
          { t: "Rankings & seasons", d: "One fast race is good. Being consistently fast is better. Climb the leaderboard and prove where you rank." },
        ]} />
      </Section>

      <Checker />

      {/* RACE TYPES */}
      <Section dark>
        <Eyebrow onDark>PICK YOUR RACE</Eyebrow>
        <h2 className={`${display} text-4xl sm:text-5xl lg:text-6xl leading-[0.9] mb-10`} style={balance}>
          Start free. Race for more.
        </h2>
        <CardGridResponsive cols="repeat(3,minmax(0,1fr))" items={[
          { t: "Free races", d: "Learn the game, sharpen your speed, and compete without an entry fee." },
          { t: "Cash races", d: "Put your skills to the test against other racers for larger prize pools." },
          { t: "Ranked races", d: "Compete against racers at your level and climb the season leaderboard." },
        ]} />
      </Section>

      <Checker />

      {/* LEAGUE */}
      <Section>
        <Eyebrow>PRACTICE & EDUTAINMENT</Eyebrow>
        <h2 className={`${display} text-4xl sm:text-5xl lg:text-6xl leading-[0.9] mb-5`} style={balance}>
          Join the Text Racing league
        </h2>
        <p className={`${body} text-[15px] leading-relaxed mb-12 max-w-3xl`} style={{ color: MUTEDP }}>
          Technology is changing how people learn, practice, and compete. Text Racing creates new ways to sharpen texting speed, accuracy, and knowledge through fun, competitive experiences.
        </p>
        <div className="grid gap-8 md:grid-cols-3">
          {[
            { tag: "Knowledge", n: "01", t: "Trivia & Education Races", d: "Put your knowledge and your thumbs to the test in themed races built around trivia, educational topics, and general knowledge." },
            { tag: "Resources", n: "02", t: "Text Racing Magazine & Materials", d: "Explore monthly magazines, booklets, challenges, and practice materials designed to help racers learn, improve, and stay engaged." },
            { tag: "Community", n: "03", t: "Live Group Competitions", d: "Bring Text Racing to schools, teams, clubs, and organizations with live, knowledge-based competitions that turn learning into a competitive sport." },
          ].map((s, i) => (
            <div key={i} className="pt-4 transition-colors duration-200 hover:[border-top-color:#0b0b0c]"
              style={{ borderTop: `2px solid ${LINEP}` }} data-testid={`league-${i}`}>
              <div className={`${mono} text-[10px] uppercase tracking-[0.2em] mb-2`} style={{ color: MUTEDP }}>{s.tag}</div>
              <div className={`${mono} text-3xl mb-3`} style={{ color: INK }}>{s.n}</div>
              <h3 className={`${display} text-2xl leading-none mb-2`} style={balance}>{s.t}</h3>
              <p className={`${body} text-[15px] leading-relaxed`} style={{ color: MUTEDP }}>{s.d}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-14"><Solid to={join} testid="league-cta">Join the League</Solid></div>
      </Section>

      <Checker />

      {/* PUBLICATION */}
      <Section dark id="publication">
        <div className="grid grid-cols-1 md:grid-cols-[55fr_45fr] gap-10 md:gap-14 md:items-center">
          <div>
            <Eyebrow onDark>THE PUBLICATIONS</Eyebrow>
            <h2 className={`${display} text-4xl sm:text-5xl lg:text-6xl leading-[0.9] mb-5`} style={balance}>
              Digital sport for a new world
            </h2>
            <p className={`${body} text-[15px] leading-relaxed mb-6 max-w-xl`} style={{ color: MUTED }}>
              Read the works behind Text Racing and <span style={{ color: PAPER }}>Texting as a Sport (TaaS)</span> -
              the archived <span style={{ color: PAPER }}>2021</span> original alongside the current <span style={{ color: PAPER }}>2026</span> revision.
            </p>
            <Solid to="/publication" dark testid="publication-cta">View the Publications</Solid>
          </div>
          <Link to="/publication" data-testid="publication-cover" className="block transition-transform duration-200 hover:-translate-y-1">
            <div className="p-8 border" style={{ background: PAPER, color: INK, borderColor: LINEP }}>
              <div className={`${mono} text-[10px] uppercase tracking-[0.2em] mb-6`} style={{ color: MUTEDP }}>2021 &amp; 2026 Editions</div>
              <FileText size={34} style={{ color: INK }} />
              <div className={`${display} text-3xl leading-[0.9] mt-4 mb-3`} style={balance}>Text Racing</div>
              <p className={`${body} text-[13px] leading-relaxed`} style={{ color: MUTEDP }}>A Digital Sport for a New World - © Mozo Marketing</p>
              <div className={`${mono} text-[11px] uppercase tracking-[0.15em] mt-6 inline-flex items-center gap-2`} style={{ color: INK }}>
                View both editions →
              </div>
            </div>
          </Link>
        </div>
      </Section>

      <Checker />

      {/* FAIR PLAY */}
      <Section dark>
        <Eyebrow onDark>FAIR PLAY</Eyebrow>
        <h2 className={`${display} text-4xl sm:text-5xl lg:text-6xl leading-[0.9] mb-5`} style={balance}>
          Built for fair competition.
        </h2>
        <p className={`${body} text-[15px] leading-relaxed mb-12 max-w-3xl`} style={{ color: MUTED }}>
          Every race should come down to one thing: who can text the fastest and most accurately. That's why Text Racing is built to keep competition fair.
        </p>
        <CardGridResponsive cols="repeat(4,minmax(0,1fr))" mdBreak items={[
          { t: "Free to start", d: "Race for free every day. Paid races always show the entry price and potential prize before you join." },
          { t: "Fair-play protection", d: "Automated detection helps identify macros, scripts, autocorrect manipulation, and other unfair advantages." },
          { t: "13+ to play", d: "Text Racing is designed for teens and adults. Age requirements may apply to certain race types, prizes, or features." },
          { t: "Real support", d: "Have a question about your account, a race, or your winnings? Real support is available when you need it." },
        ]} />
      </Section>

      <Checker />

      {/* FAQ */}
      <Section>
        <Eyebrow>COMMON QUESTIONS</Eyebrow>
        <h2 className={`${display} text-4xl sm:text-5xl lg:text-6xl leading-[0.9]`} style={balance}>
          Frequently asked questions
        </h2>
        <Faq />
      </Section>

      <Checker />

      {/* TEXT RACING EDUTAINMENT */}
      <Section dark id="edutainment">
        <Eyebrow onDark>TEXT RACING EDUTAINMENT</Eyebrow>
        <h2 className={`${display} text-4xl sm:text-5xl lg:text-6xl leading-[0.9] mb-8`} style={balance}>
          The booklet series
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-[40fr_60fr] gap-8 md:gap-14 md:items-center">
          {/* Front cover */}
          <Link to={activateTo} data-testid="edutainment-cover" className="block group max-w-[220px] mx-auto md:mx-0">
            <div className="relative overflow-hidden border shadow-2xl" style={{ borderColor: LINEP, background: PAPER }}>
              <img src={EDU_COVER} alt="Text Racing Edutainment booklet front cover"
                className="w-full h-auto block transition-transform duration-300 group-hover:scale-[1.02]" />
            </div>
          </Link>
          {/* Copy + pricing */}
          <div>
            <p className={`${body} text-[15px] leading-relaxed mb-7`} style={{ color: MUTED }}>
              Our booklet series lets members purchase physical and online media booklets - a new way to enhance
              <span style={{ color: PAPER }}> literacy</span> across various subject matter while practicing and sharpening their
              <span style={{ color: PAPER }}> Text Racing skills</span> in the process, with the opportunity to win
              <span style={{ color: PAPER }}> cash and prizes</span> from our advertisers and sponsors.
            </p>
            <div className="p-6 border mb-7 flex items-center gap-6" style={{ background: PAPER, color: INK, borderColor: LINEP }}>
              <div className="text-center shrink-0">
                <div className={`${display} text-5xl leading-none`} style={balance}>$12.99</div>
                <div className={`${mono} text-[10px] uppercase tracking-[0.15em] mt-1`} style={{ color: MUTEDP }}>to activate</div>
              </div>
              <div className="h-12 w-px" style={{ background: LINEP }} />
              <div className={`${body} text-[14px] leading-snug`} style={{ color: INK }}>
                The <span className="font-bold">$12.99</span> activates your booklet and assigns it to your phone - and includes <span className="font-bold">$15.00 in race credits</span> to compete in Text Racing contests.
              </div>
            </div>
            <Link to={activateTo} data-testid="edutainment-cta"
              className={`${body} font-semibold inline-flex items-center gap-2 px-7 py-3.5 text-sm tracking-wide transition-opacity duration-150 hover:opacity-80`}
              style={{ background: PAPER, color: INK }}>
              <BookOpen size={16} /> Activate for $12.99
            </Link>
          </div>
        </div>
      </Section>

      <Checker />

      {/* FINAL CTA */}
      <Section dark className="text-center">
        <h2 className={`${display} text-5xl sm:text-6xl lg:text-7xl leading-none mb-4`} style={balance}>Think you're fast?</h2>
        <p className={`${body} text-lg font-semibold mb-4`} style={{ color: PAPER }}>There's only one way to prove it.</p>
        <p className={`${body} text-[15px] leading-relaxed mb-8 max-w-xl mx-auto`} style={{ color: MUTED }}>
          Race real players. Test your speed. Climb the rankings. Win cash and prizes.
        </p>
        <div className={`${display} text-3xl sm:text-4xl mb-8`} style={balance}>
          Text Fast. <Highlight dark>Finish First.</Highlight>
        </div>
        <Solid to={start} dark testid="final-cta">{authed ? "Enter Dashboard" : "Start Racing"}</Solid>
      </Section>

      {/* FOOTER */}
      <footer className="px-6 md:px-8 border-t" style={{ background: INK, borderColor: LINE }}>
        <div className="mx-auto py-10 flex flex-col md:flex-row items-center justify-between gap-6" style={{ maxWidth: 1120 }}>
          <img src={LOGO_WHITE} alt="Text Racing" className="h-6 w-auto" />
          <nav className={`${mono} text-[11px] uppercase tracking-[0.15em] flex flex-wrap items-center gap-x-6 gap-y-2 justify-center`} style={{ color: MUTED }}>
            {[
              { l: "Support", to: null },
              { l: "Privacy", to: "/privacy" },
              { l: "Terms", to: "/terms" },
              { l: "Age Rating: 13+", to: null },
              { l: "Publication", to: "/publication" },
            ].map(({ l, to }) => (
              to
                ? <Link key={l} to={to} data-testid={`footer-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="hover:opacity-80 transition-opacity" style={{ color: MUTED }}>{l}</Link>
                : <a key={l} href="#" className="hover:opacity-80 transition-opacity" style={{ color: MUTED }}>{l}</a>
            ))}
          </nav>
          <span className={`${mono} text-[11px]`} style={{ color: MUTED }}>© 2026 Text Racing</span>
        </div>
      </footer>

      {/* STICKY MOBILE CTA */}
      <div className="md:hidden fixed inset-x-0 bottom-0 z-50 p-3 border-t transition-transform duration-300"
        style={{ background: "rgba(11,11,12,0.92)", backdropFilter: "blur(10px)", borderColor: LINE,
          transform: showBar ? "translateY(0)" : "translateY(120%)" }}>
        <Link to={start} data-testid="sticky-cta"
          className={`${body} font-semibold block text-center px-7 py-3.5 text-sm`}
          style={{ background: PAPER, color: INK }}>
          Start Racing
        </Link>
      </div>
    </div>
  );
}

/* Responsive tile grid that collapses to 1 column on small screens. */
function CardGridResponsive({ cols, items, mdBreak }) {
  return (
    <>
      <style>{`
        .tr-grid{display:grid;gap:1px;background:${LINEP};grid-template-columns:1fr}
        @media(min-width:${mdBreak ? 920 : 820}px){.tr-grid{grid-template-columns:${cols}}}
      `}</style>
      <div className="tr-grid">
        {items.map((it, i) => (
          <div key={i} className="p-6 md:p-8" style={{ background: PAPER }} data-testid={`card-${i}`}>
            <h3 className={`${display} text-2xl leading-none mb-3`} style={{ color: INK, ...balance }}>{it.t}</h3>
            <p className={`${body} text-[15px] leading-relaxed`} style={{ color: MUTEDP }}>{it.d}</p>
          </div>
        ))}
      </div>
    </>
  );
}
